import React, { useState } from "react";
import "./FAQModal.scss";

const FAQ_DATA = {
  General: [
    {
      q: "Who can assign a stage?",
      a: "The Approver and Creator can assign stages to any project member except Viewers and Clients. A Doer cannot assign members and is automatically assigned as the stage owner.",
    },
    {
      q: "Who can update a stage?",
      a: "Stage details can be updated by the Approver and the assigned Stage Owner.",
    },
    {
      q: "Who can update stage status?",
      a: "Stage status can be updated by the Stage Owner, Approver, and Creator.",
    },
    {
      q: "Who can assign a task?",
      a: "The Approver and Creator can assign tasks to any project member except Viewers and Clients. A Doer cannot assign members and is automatically assigned as the task owner.",
    },
    {
      q: "Who can update a task?",
      a: "Task details can be updated by the Approver, Stage Owner, and Task Owner.",
    },
    {
      q: "Who can update task status?",
      a: "Task status can be updated by the Approver, Stage Owner, Task Owner, and Creator.",
    },
    {
      q: "Who can access To-Do checklists?",
      a: "To-Do items can be accessed by the Approver, Stage Owner, Task Owner, and Creator. The Creator can view and access the checklist but cannot delete it.",
    },
    {
      q: "What is a Project?",
      a: "A project is the main workspace where all work is managed. It contains stages, tasks, members, and project-level settings.",
    },
    {
      q: "What is the project hierarchy?",
      a: "The hierarchy follows this order: Project → Stages → Tasks → To-Do List. This structure helps break work into manageable levels.",
    },
  ],

  Stages: [
    {
      q: "Who can assign a stage?",
      a: "The Approver and Creator can assign stages to any project member except Viewers and Clients. A Doer cannot assign members and is automatically assigned as the stage owner.",
    },
    {
      q: "Who can update a stage?",
      a: "Stage details can be updated by the Approver and the assigned Stage Owner.",
    },
    {
      q: "Who can update stage status?",
      a: "Stage status can be updated by the Stage Owner, Approver, and Creator.",
    },
    {
      q: "Who can access To-Do checklists?",
      a: "To-Do items can be accessed by the Approver, Stage Owner, Task Owner, and Creator. The Creator can view and access the checklist but cannot delete it.",
    },
    {
      q: "What is a Stage?",
      a: "A stage is the first level of task organization inside a project. It represents a phase of work and contains multiple tasks and todo/checklist.",
    },
    {
      q: "Can I assign members to a stage?",
      a: "Yes, any member can be assigned to a stage to oversee or manage work happening in that stage.",
    },
  ],

  Tasks: [
    {
      q: "Who can assign a task?",
      a: "The Approver and Creator can assign tasks to any project member except Viewers and Clients. A Doer cannot assign members and is automatically assigned as the task owner.",
    },
    {
      q: "Who can update a task?",
      a: "Task details can be updated by the Approver, Stage Owner, and Task Owner.",
    },
    {
      q: "Who can update task status?",
      a: "Task status can be updated by the Approver, Stage Owner, Task Owner, and Creator.",
    },
    {
      q: "What is a Task?",
      a: "A task is a unit of work inside a stage and can be assigned to one person who is responsible for completing it.",
    },
    {
      q: "What is a To-Do list?",
      a: "A to-do list is a checklist inside a task used to break work into smaller actionable steps.",
    },
  ],

  Roles: [
    {
      q: "What roles are available?",
      a: "Approver has full access. Creator has full access except deleting projects or removing members. Doer can update assigned tasks only. Viewer or Client has read-only access.",
    },
    {
      q: "What can a Doer do?",
      a: "A Doer can view and update only the tasks assigned to them and cannot modify project structure.",
    },
  ],

  Views: [
    {
      q: "What is Kanban View?",
      a: "Kanban View displays stages horizontally and allows drag-and-drop of stages and tasks for easy workflow management.",
    },
    {
      q: "What is Doer View?",
      a: "Doer View shows only the tasks assigned to the user without displaying stages enabling drag and drop on tasks.",
    },
  ],

  Project: [
    {
      q: "How do I delete a project?",
      a: "All stages inside the project must be deleted first. Once the project is empty, it can be deleted.",
    },
    {
      q: "What happens if I remove a member assigned to a task?",
      a: "The stage becomes unassigned. You can reassign it to another member or delete it.",
    },
  ],
};

export default function FAQModal({ isOpen, onClose }) {
  const [activeSection, setActiveSection] = useState("All");
  const [openIndex, setOpenIndex] = useState(null);

  if (!isOpen) return null;

  const itemsToRender =
    activeSection === "All"
      ? Object.entries(FAQ_DATA).flatMap(([section, items]) =>
          items.map((item) => ({ ...item, section })),
        )
      : FAQ_DATA[activeSection];

  return (
    <div className="FAQModal-backdrop" onClick={onClose}>
      <div className="FAQModal-container" onClick={(e) => e.stopPropagation()}>
        {/* Sidebar */}
        <div className="FAQModal-sidebar">
          <button
            className={`FAQModal-section ${
              activeSection === "All" ? "active" : ""
            }`}
            onClick={() => {
              setActiveSection("All");
              setOpenIndex(null);
            }}
          >
            All
          </button>

          {Object.keys(FAQ_DATA).map((section) => (
            <button
              key={section}
              className={`FAQModal-section ${
                activeSection === section ? "active" : ""
              }`}
              onClick={() => {
                setActiveSection(section);
                setOpenIndex(null);
              }}
            >
              {section}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="FAQModal-content">
          {itemsToRender.map((item, index) => {
            const isOpenItem = openIndex === index;

            return (
              <div
                key={index}
                className={`FAQModal-item ${isOpenItem ? "open" : ""}`}
              >
                <div
                  className="FAQModal-question"
                  onClick={() => setOpenIndex(isOpenItem ? null : index)}
                >
                  <span>{item.q}</span>
                  <span className="FAQModal-icon">
                    {isOpenItem ? "−" : "+"}
                  </span>
                </div>

                <div className="FAQModal-answer">{item.a}</div>
              </div>
            );
          })}
        </div>

        {/* Cancel Button */}
        <button className="FAQModal-cancel" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
